/** Copyright 2024 SAP SE or an SAP affiliate company. All rights reserved. */

import { useAppSelector } from '@/hooks';
import { useTranslations } from '@/hooks/useTranslations';
import { PhaseState } from '@/interfaces/product';
import { useGetCommodityCodeRunExistQuery, useGetUsersQuery } from '@/store/apis';
import { actions, selectIsProductConfigurationChanged } from '@/store/Products';
import { WizardStepChangeEventDetail } from '@ui5/webcomponents-fiori/dist/Wizard';
import { Ui5CustomEvent, Wizard, WizardDomRef, WizardStep } from '@ui5/webcomponents-react';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import WarningMessageBox from '../components/WarningMessageBox';

const WizardFunnel = () => {
    const [currentPercentage, setCurrentPercentage] = useState<number>(0);
    const productList = useAppSelector(state => state.productList);
    const dispatch = useDispatch();
    const { t } = useTranslations();
    const excludePhase = ['REVIEW', 'INTRODUCTION', 'ERP'];
    const { currentRunId, currentPhase, aPhases, aSubStepsForCurrentPhase, currentStep, isLaunchTriggeredFromUI } = productList;
    const [wizardKey, setWizardKey] = useState(0); // Key for forcing re-render
    const [navigatedPhaseId, setNavigatedPhaseId] = useState<string | null>(null);
    const isProductConfigurationChanged = useSelector(selectIsProductConfigurationChanged);
    const { data: users = [] } = useGetUsersQuery(currentRunId, {
        skip: !currentRunId,
    });

    const { data: isCommodityCodeExist } = useGetCommodityCodeRunExistQuery(currentRunId, {
        skip: !currentRunId,
    });

    const handlePhaseChange = (e: Ui5CustomEvent<WizardDomRef, WizardStepChangeEventDetail>) => {
        const phaseId = e.detail.step.dataset.step ?? '';
        if (phaseId === currentPhase.id) return;
        if (isProductConfigurationChanged) {
            setWizardKey(prevKey => prevKey + 1); // Force re-render workaround
            setNavigatedPhaseId(phaseId);
        } else {
            dispatch(actions.setCurrentPhaseById({ id: phaseId }));
        }
    };
    const handleConfirm = () => {
        if (navigatedPhaseId) {
            dispatch(actions.setCurrentPhaseById({ id: navigatedPhaseId }));
            setNavigatedPhaseId(null);
        }
    };
    const handleCancel = () => {
        setNavigatedPhaseId(null);
    };

    useEffect(() => {
        const totalSteps = aSubStepsForCurrentPhase.length;

        const subStepAnswered = aSubStepsForCurrentPhase
            ? aSubStepsForCurrentPhase.filter(subStep => subStep.answered || (subStep.translationNameKey === 'ENABLEMENT' && isCommodityCodeExist))
                  .length
            : 0;

        if (users.length && currentPhase.translationNameKey === 'USERS') {
            setCurrentPercentage(100);
            return;
        }
        if (subStepAnswered === 0) {
            setCurrentPercentage(0);
        } else {
            setCurrentPercentage(Math.floor((subStepAnswered / totalSteps) * 100) || 0);
        }
    }, [currentStep, users]);

    useEffect(() => {
        if (isLaunchTriggeredFromUI) {
            setCurrentPercentage(100);
        }
    }, [isLaunchTriggeredFromUI]);

    return (
        <div data-testid="wizard-funnel">
            <Wizard className="wizard-funnel" onStepChange={handlePhaseChange} key={wizardKey}>
                {(aPhases || []).map((phase: PhaseState) => {
                    const isCurrentPhase = currentPhase.id === phase.id;
                    const isExcludePhase = !excludePhase.includes(currentPhase.translationNameKey);
                    return (
                        <WizardStep
                            icon={phase.iconName}
                            key={phase.id}
                            titleText={t({ key: phase.translationNameKey, defaultText: phase.name })}
                            selected={isCurrentPhase}
                            data-step={phase.id}
                            hidden={true}
                            subtitleText={isCurrentPhase && isExcludePhase ? t('PROGRESS_PERCENT', currentPercentage.toString()) : undefined}
                        />
                    );
                })}
            </Wizard>
            {navigatedPhaseId && (
                <WarningMessageBox open dataTestId="continue-warning-phase-change" onConfirm={handleConfirm} onCancel={handleCancel} />
            )}
        </div>
    );
};
export default WizardFunnel;